import { Component, ElementRef, Inject, OnInit } from "@angular/core";
import { AbstractControl } from "@angular/forms";
import { ButtonDirective } from "@ankaadia/ankaadia-shared-frontend";
import { FieldType, FormlyModule } from "@ngx-formly/core";
import { PrimeTemplate } from "primeng-v17/api";
import { DialogService } from "primeng-v17/dynamicdialog";
import { EditorModule } from "primeng-v17/editor";
import { TooltipModule } from "primeng-v17/tooltip";
import Quill from "quill";
import { filter, startWith } from "rxjs";
import { CandidateTranslationDialogComponent } from "../../features/candidates/candidate-translation-dialog/candidate-translation-dialog.component";
import type { TranslationMapping } from "../../features/metered-translations/metered-translations.model";
import { MeteredTranslationsService } from "../../features/metered-translations/metered-translations.service";

@Component({
  selector: "app-formly-rich-text",
  templateUrl: "./formly-rich-text.component.html",
  imports: [ButtonDirective, TooltipModule, EditorModule, FormlyModule, PrimeTemplate],
})
export class FormlyRichTextComponent extends FieldType implements OnInit {
  /** @deprecated Use the new-ui rich-text component with propertyPath instead */
  editor: Quill;
  /** @deprecated Use the new-ui rich-text component with propertyPath instead */
  translationMapping: TranslationMapping;
  /** @deprecated Use the new-ui rich-text component with propertyPath instead */
  currentTranslation: string;

  constructor(
    protected readonly elementRef: ElementRef,
    private readonly translationService: MeteredTranslationsService,
    @Inject(DialogService)
    private readonly dialogService: DialogService
  ) {
    super();
  }

  ngOnInit(): void {
    this.translationService.candidatePropertyTranslationsLoaded$
      .pipe(
        startWith(this.translationService.candidatePropertyTranslationsLoaded$.value),
        filter((x) => x === this.props.candidateId)
      )
      .subscribe(() => {
        this.setUpTranslation();
      });
  }

  /** @deprecated Use the new-ui rich-text component with propertyPath instead */
  updateEditor(editor: Quill): void {
    if (editor) {
      this.editor = editor;
      const delta = this.getDeltaControl()?.value ?? this.getContentControl()?.value ?? null;
      this.editor.setContents(delta);
    }
  }

  /** @deprecated Use the new-ui rich-text component with propertyPath instead */
  updateHtml(): void {
    if (this.props?.maxLength && this.editor.getLength() > this.props.maxLength) {
      this.editor.deleteText(this.props.maxLength, this.editor.getLength());
    }

    this.getContentControl().setValue(this.editor.getText());
    this.getContentControl().markAsDirty();
    this.getDeltaControl().setValue(this.editor.getContents());
  }

  /** @deprecated Use the new-ui rich-text component with propertyPath instead */
  openDialog(event: Event): void {
    this.dialogService.open(CandidateTranslationDialogComponent, {
      closeOnEscape: false,
      dismissableMask: false,
      styleClass: "p-dialog-compact",
      modal: true,
      width: "50%",
      appendTo: this.getElementToAppend(event.target as Element),
      data: {
        sourceText: { delta: this.editor?.getContents() },
        itemId: this.translationMapping.id,
      },
    });
  }

  private getElementToAppend(element: Element): Element {
    let lastElement: Element = element;
    while (element && typeof element.className === "string" && !element.className.includes("layout-wrapper")) {
      lastElement = element;
      element = element.parentElement;
    }
    return element ?? lastElement;
  }

  private setUpTranslation(): void {
    this.translationMapping = null;
    const experienceId = this.model?.id;

    if (!experienceId) return;
    const currentTranslation = this.translationService
      .getFieldTranslationsWithState()
      .find(
        (t) =>
          t.propertyPath.some((p) => p === experienceId) &&
          t.propertyPath.some((p) => p === this.props.translationPrefixIdentifier)
      );

    if (currentTranslation) {
      this.translationMapping = { id: currentTranslation.id, formControl: this.field.formControl };
      this.currentTranslation = currentTranslation.targetText;
    }
  }

  protected getContentControl(): AbstractControl<string> {
    return this.form.get(this.props.contentControlName);
  }

  protected getDeltaControl(): AbstractControl {
    return this.form.get(this.props.deltaControlName);
  }
}
